import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AuthorStories({ author, onBack, onViewStory }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/authors/${encodeURIComponent(author)}/stories`);
        if (response.data.success) {
          setStories(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching author stories:', err);
        setError('Failed to load stories by this author');
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, [author]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Recent';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Recent';
    }
  };

  // Build slug-based URL for a story
  const storyHref = (s) => {
    if (s.slug) return `/story/${encodeURIComponent(s.slug)}`;
    if (s.story_id) return `/story/${encodeURIComponent(s.story_id)}`;
    return '#';
  };

  return (
    <div className="author-stories-page">
      {/* SEO Meta Tags are emitted by App.js (single source of truth) */}

      <div className="author-stories-header">
        <button className="author-back-btn" onClick={onBack}>← Back</button>
        <div className="author-stories-title-section">
          <h1>✍️ Stories by {author}</h1>
          {!loading && !error && (
            <span className="author-stories-count">{stories.length} {stories.length === 1 ? 'story' : 'stories'}</span>
          )}
        </div>
      </div>

      {/* Author bio section for E-E-A-T signals */}
      {!loading && !error && stories.length > 0 && (
        <section className="author-bio-section" aria-label={`About ${author}`}>
          <h2>About {author}</h2>
          <p>
            <strong>{author}</strong> is a writer on StoryMeal with {stories.length} published {stories.length === 1 ? 'story' : 'stories'}.
            Read all of {author}'s work for free, and discover new stories from this talented writer.
          </p>
        </section>
      )}

      {loading ? (
        <div className="author-loading">Loading stories...</div>
      ) : error ? (
        <div className="author-error-message">{error}</div>
      ) : stories.length === 0 ? (
        <div className="author-empty">
          <div className="author-empty-icon">📖</div>
          <h3>No stories found</h3>
          <p>This author hasn't published any stories yet.</p>
        </div>
      ) : (
        <div className="author-stories-grid">
          {stories.map(story => (
            <Link
              key={story.story_id || story.id}
              to={storyHref(story)}
              className="author-story-card"
              title={`${story.title} by ${story.author || 'Anonymous'} - read online`}
            >
              <div className="author-story-card-content">
                <h3>{story.title || 'Untitled'}</h3>
                <p className="author-story-excerpt">
                  {(story.content_text || '').substring(0, 150) || 'No preview available'}...
                </p>
                <div className="author-story-meta">
                  <span>❤️ {story.like_count || 0}</span>
                  <span>👁️ {story.view_count || 0}</span>
                  <span>💬 {story.comment_count || 0}</span>
                  <span>📅 {formatDate(story.created_at)}</span>
                </div>
                {story.genre && (
                  <span className="author-story-genre">{story.genre}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default AuthorStories;